import type {
  NamedTableViewJSON,
  TableColumnPreferenceJSON,
} from "_shared/_views/tables/_schemas/table-column-preferences-schema";

/**
 * Which view a list opens, and what of a view is kept (#6714). Pure, so each rule is tested on its
 * own; `useCurrentTableView` puts them on a page.
 *
 * A list's view is its URL (#6712): the tab is a path segment, and chips, column filters, sort and
 * page size are search parameters. So a stored view is a path, and opening it is navigating there.
 */

/**
 * The parameters no view keeps: the search text and the page number. A view kept with them would
 * open on a search the reader typed once, or on page 4 of a list that has since shrunk.
 */
export const NEVER_REMEMBERED_PARAMETERS: readonly string[] = ["globalFilter", "query", "search", "pageIndex", "page"];

/**
 * A search parameter that names the page's Default view in a link, `?view=default`, so the link
 * opens the Default view even for a reader whose plain visit opens another. A page with chips may
 * spell its default its own way as well, as Proposals does with `stage=open`.
 */
export const DEFAULT_VIEW_PARAMETER = "view";
export const DEFAULT_VIEW_VALUE = "default";

/** The path of a list's view as the page keeps it: the tab path and the view's own parameters. */
export type ListViewPath = Readonly<{ tabPath: string; search: string }>;

/**
 * A view a bare visit opens, and the saved view it is: the starred view's id, or the id the current
 * view started from (null for the Default view).
 */
export type StoredListView = ListViewPath & Readonly<{ viewId: string | null }>;

/**
 * Whether a URL names no view at all: the list's own path, with no tab and no parameter. Only such
 * a URL -- the side nav's `/proposals` -- opens a stored view. Every other URL names its view, and is
 * shown exactly as it says: an assistant `listUrl`, a dashboard card, an email, a shared link.
 */
export function isBareListUrl(basePath: string, pathname: string, search: string): boolean {
  return trimSlash(pathname) === trimSlash(basePath) && new URLSearchParams(search).size === 0;
}

/**
 * The view a bare visit to the list opens: the reader's starred view, else the view they left it in,
 * else none -- the page's Default.
 */
export function viewForBareVisit(table: TableColumnPreferenceJSON | undefined): StoredListView | undefined {
  const starred: NamedTableViewJSON | undefined = table?.views?.find((view) => view.isStarred);
  const state = starred?.state ?? table?.currentView?.state;
  if (!state?.tabPath) return undefined;
  const viewId = starred ? starred.id : (table?.currentView?.viewId ?? null);
  return { tabPath: state.tabPath, search: state.search ?? "", viewId };
}

/**
 * What of the location a view keeps: its path, and its search parameters less the ones no view
 * keeps and the Default-view marker.
 */
export function listViewPathOf(pathname: string, search: string): ListViewPath {
  const params = new URLSearchParams(search);
  for (const name of [...NEVER_REMEMBERED_PARAMETERS, DEFAULT_VIEW_PARAMETER]) params.delete(name);
  return { tabPath: trimSlash(pathname) || "/", search: params.toString() };
}

export function hrefOf(view: ListViewPath): string {
  return view.search ? `${view.tabPath}?${view.search}` : view.tabPath;
}

function trimSlash(path: string): string {
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

/** What the Views menu calls the page's own view, which every reader has and nobody can edit (#6717). */
export const DEFAULT_VIEW_NAME = "Default view";

/** The longest name a saved view may have, as the account keeps it. */
export const MAX_VIEW_NAME_LENGTH = 80;

/**
 * The link that opens the page's Default view: its path with `?view=default`, so a reload shows it
 * even for a reader whose plain visit opens a starred view.
 */
export function defaultViewHref(defaultPath: string): string {
  const url = new URL(defaultPath, "https://list.invalid");
  url.searchParams.set(DEFAULT_VIEW_PARAMETER, DEFAULT_VIEW_VALUE);
  return `${url.pathname}${url.search}`;
}

/** Whether two views show the same list: the same tab, and the same parameters in any order. */
export function isSameListView(left: ListViewPath, right: ListViewPath): boolean {
  return trimSlash(left.tabPath) === trimSlash(right.tabPath) && sortedSearch(left.search) === sortedSearch(right.search);
}

/**
 * Whether two Show/hide columns choices show different columns. A column a choice leaves out shows as
 * the table shows it by default, so `{}` and `{ name: true }` are the same choice for a column that
 * is shown anyway. Only the columns the table has count.
 */
export function columnChoicesDiffer(
  left: Readonly<Record<string, boolean>>,
  right: Readonly<Record<string, boolean>>,
  columnIds: readonly string[],
  isShownByDefault: (columnId: string) => boolean,
): boolean {
  return columnIds.some((id) => (left[id] ?? isShownByDefault(id)) !== (right[id] ?? isShownByDefault(id)));
}

/** The widths and order of a table's columns, as a view keeps them. */
export type ColumnLayoutChoice = Readonly<{
  columnSizing: Readonly<Record<string, number>>;
  columnOrder: readonly string[];
}>;

/**
 * Whether two column layouts differ in a column the table has: a width dragged, or columns moved.
 * No width is the page's own width, and an empty order is the page's own order.
 */
export function columnLayoutsDiffer(
  left: ColumnLayoutChoice,
  right: ColumnLayoutChoice,
  columnIds: readonly string[],
): boolean {
  if (columnIds.some((id) => left.columnSizing[id] !== right.columnSizing[id])) return true;
  const ordered = (order: readonly string[]) => JSON.stringify(order.filter((id) => columnIds.includes(id)));
  return ordered(left.columnOrder) !== ordered(right.columnOrder);
}

/**
 * Why `name` cannot be given to a saved view, or undefined when it can. The account refuses the same
 * names; asking first lets the dialog say so beside the field.
 */
export function viewNameProblem(
  name: string,
  views: readonly Pick<NamedTableViewJSON, "id" | "name">[],
  exceptId?: string,
): string | undefined {
  const trimmed = name.trim();
  if (trimmed === "") return "Give the view a name.";
  if (trimmed.length > MAX_VIEW_NAME_LENGTH) return `Use ${MAX_VIEW_NAME_LENGTH} characters or fewer.`;
  const lower = trimmed.toLowerCase();
  if (lower === "default" || lower === DEFAULT_VIEW_NAME.toLowerCase()) return "That is the page's own view. Choose another name.";
  const taken = views.some((view) => view.id !== exceptId && view.name.trim().toLowerCase() === lower);
  return taken ? "You already have a view with this name." : undefined;
}

function sortedSearch(search: string): string {
  return [...new URLSearchParams(search)].map(([name, value]) => `${name}=${value}`).sort().join("&");
}
